import { useCallback, useEffect, useState } from 'react'
import {
  SYSTEM_HEALTH_STATUS_LABELS,
  OsmQueueStatsDto,
  SystemHealthDto,
  SystemHealthStatus,
} from '@ivy-monorepo/shared'
import { systemHealthService } from '../../services/system-health.service'
import Table from '../../components/Table'

const REFRESH_INTERVAL_MS = 60_000

function formatCheckedAt(value: string): string {
  return new Date(value).toLocaleString()
}

function formatLatency(latencyMs?: number | null): string {
  return latencyMs == null ? '—' : `${latencyMs} ms`
}

function formatEpochMs(value: number | null): string {
  return value == null ? '—' : new Date(value).toLocaleString()
}

function formatCount(value: number): string {
  return value.toLocaleString()
}

function priorityLabel(priority: number): string {
  return priority === 0 ? 'Critical' : priority === 1 ? 'High' : 'Medium'
}

const SystemStatusPage = () => {
  const [health, setHealth] = useState<SystemHealthDto | undefined>(undefined)
  const [osmQueue, setOsmQueue] = useState<OsmQueueStatsDto | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchStatus = useCallback(async (refresh = false) => {
    if (refresh) {
      setRefreshing(true)
    } else {
      setLoading(true)
    }

    try {
      const [healthResponse, osmResponse] = await Promise.all([
        systemHealthService.getSystemHealth(refresh),
        systemHealthService.getOsmQueueStats(),
      ])
      setHealth(healthResponse)
      setOsmQueue(osmResponse)
      setError(null)
    } catch (err) {
      console.log('Failed to fetch system status', err)
      setError('Failed to load system status.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    fetchStatus()
    const interval = setInterval(() => fetchStatus(), REFRESH_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [fetchStatus])

  const overallStatus = health?.status ?? SystemHealthStatus.Unhealthy

  return (
    <div className="system-status-page">
      <div className="system-status-header">
        <div>
          <h2>System status</h2>
          <p className="system-status-subtitle">
            Infrastructure health for backend services and external integrations.
          </p>
        </div>
        <button
          type="button"
          className="styled-button sm"
          disabled={loading || refreshing}
          onClick={() => fetchStatus(true)}
        >
          {refreshing ? 'Refreshing…' : 'Refresh now'}
        </button>
      </div>

      {error && <div className="system-status-error">{error}</div>}

      {loading && !health ? (
        <></>
      ) : health ? (
        <>
          <div className={`system-status-overview-card system-status-overview-card--${overallStatus}`}>
            <div className="system-status-overview-card__top">
              <div className="system-status-overview-card__heading">
                <span className="system-status-overview-card__label">Overall status</span>
                <span className="system-status-overview-card__status-text">
                  {SYSTEM_HEALTH_STATUS_LABELS[overallStatus]}
                </span>
              </div>
              <div className="system-status-overview-card__meta">
                Last checked {formatCheckedAt(health.checkedAt)}
                {health.cached ? ' (cached)' : ''}
              </div>
            </div>

            <div className="system-status-overview-card__description">
              <span className="system-status-overview-card__hint">
                This view aggregates dependency probes (DB, storage, messaging, Google APIs) and job health.
              </span>
            </div>
          </div>

          <Table
            id="system-health"
            name="System health"
            keyField="id"
            initialSortColumn="priority"
            columns={[
              { label: ``, accessor: 'priority', type: 'interval', className: 'system-health-priority-col' },
              { label: `Component`, accessor: 'label' },
              { label: `Status`, accessor: 'status', type: 'enum', enumName: 'SystemHealthStatus' },
              { label: `Latency`, accessor: 'latency' },
              { label: `Priority`, accessor: 'priorityLabel' },
              { label: `Details`, accessor: 'message' },
            ]}
            sourceData={health.components.map(component => ({
              id: component.id,
              label: component.label,
              status: component.status,
              latency: formatLatency(component.latencyMs),
              priority: component.priority,
              priorityLabel: priorityLabel(component.priority),
              message: component.message || '',
            }))}
            isPropertyBarVisible={false}
            filterTerms=""
            onRowClick={() => {}}
          />

          {osmQueue && (
            <>
              <section className="osm-queue-status">
                <div className="osm-queue-status__header">
                  <h3>Venue data migration</h3>
                  <p>
                    Persistent progress for known database tiles. A migrated tile has completed its
                    first Overture sync; OSM enrichment is tracked separately.
                  </p>
                </div>

                <div
                  className="tile-migration-progress"
                  role="progressbar"
                  aria-label="Tile migration progress"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={osmQueue.tileMigration.progressPercent}
                >
                  <div
                    className="tile-migration-progress__bar"
                    style={{ width: `${osmQueue.tileMigration.progressPercent}%` }}
                  />
                </div>
                <div className="tile-migration-progress__label">
                  <strong>{osmQueue.tileMigration.progressPercent}% migrated</strong>
                  <span>
                    {formatCount(osmQueue.tileMigration.migratedTiles)} of{' '}
                    {formatCount(osmQueue.tileMigration.totalTiles)} known tiles
                  </span>
                </div>

                <div className="osm-queue-status__stats">
                  <div>
                    <span>Pending tiles</span>
                    <strong>{formatCount(osmQueue.tileMigration.pendingTiles)}</strong>
                  </div>
                  <div>
                    <span>OSM enriched tiles</span>
                    <strong>{formatCount(osmQueue.tileMigration.osmEnrichedTiles)}</strong>
                  </div>
                  <div>
                    <span>Awaiting first OSM sync</span>
                    <strong>{formatCount(osmQueue.tileMigration.awaitingOsmTiles)}</strong>
                  </div>
                  <div>
                    <span>Venues in migrated tiles</span>
                    <strong>
                      {formatCount(osmQueue.tileMigration.venuesInMigratedTiles)} /{' '}
                      {formatCount(osmQueue.tileMigration.totalVenues)}
                    </strong>
                  </div>
                  <div>
                    <span>Google-backed venues</span>
                    <strong>{formatCount(osmQueue.tileMigration.googleBackedVenues)}</strong>
                  </div>
                  <div>
                    <span>Overture-backed venues</span>
                    <strong>{formatCount(osmQueue.tileMigration.overtureBackedVenues)}</strong>
                  </div>
                  <div>
                    <span>OSM-backed venues</span>
                    <strong>{formatCount(osmQueue.tileMigration.osmBackedVenues)}</strong>
                  </div>
                </div>
              </section>

              <section className="osm-queue-status">
                <div className="osm-queue-status__header">
                  <h3>OSM enrichment queue</h3>
                  <p>
                    In-process Overpass priority queue. Pending jobs coalesce by tile; requests are
                    rate-limited to protect OSM.
                  </p>
                </div>

                <div className="osm-queue-status__stats">
                  <div>
                    <span>Pending</span>
                    <strong>
                      {osmQueue.pending} / {osmQueue.limits.maxSize}
                    </strong>
                  </div>
                  <div>
                    <span>In flight</span>
                    <strong>{osmQueue.inFlight ? 'Yes' : 'No'}</strong>
                  </div>
                  <div>
                    <span>Completed</span>
                    <strong>{osmQueue.completed}</strong>
                  </div>
                  <div>
                    <span>Failed</span>
                    <strong>{osmQueue.failed}</strong>
                  </div>
                  <div>
                    <span>Dropped (full)</span>
                    <strong>{osmQueue.droppedForCapacity}</strong>
                  </div>
                  <div>
                    <span>Last request</span>
                    <strong>{formatEpochMs(osmQueue.lastRequestAt)}</strong>
                  </div>
                  <div>
                    <span>Debounce</span>
                    <strong>{osmQueue.limits.debounceMs} ms</strong>
                  </div>
                  <div>
                    <span>Min interval</span>
                    <strong>{osmQueue.limits.minRequestIntervalMs} ms</strong>
                  </div>
                </div>

                <Table
                  id="osm-queue-pending"
                  name="Pending OSM jobs (highest priority first)"
                  keyField="worldTileId"
                  initialSortColumn="priority"
                  columns={[
                    { label: 'Tile ID', accessor: 'worldTileId' },
                    { label: 'Priority', accessor: 'priority' },
                    { label: 'Reason', accessor: 'reason' },
                    { label: 'Coalesced', accessor: 'coalesceCount' },
                    { label: 'Enqueued', accessor: 'enqueuedAtLabel' },
                  ]}
                  sourceData={osmQueue.pendingJobs.map(job => ({
                    worldTileId: job.worldTileId,
                    priority: job.priority,
                    reason: job.reason,
                    coalesceCount: job.coalesceCount,
                    enqueuedAtLabel: formatEpochMs(job.enqueuedAt),
                  }))}
                  isPropertyBarVisible={false}
                  filterTerms=""
                  onRowClick={() => {}}
                />
              </section>
            </>
          )}
        </>
      ) : null}
    </div>
  )
}

export default SystemStatusPage
